import { data, type HeadersArgs, redirect } from 'react-router';

import { embedSearchWidgetIsomorphicApiFetchWidget } from '#/modules/embedSearchWidget/api/embedSearchWidgetApiFetchWidget';
import { EmbedSearchWidgetContext } from '#/modules/embedSearchWidget/contexts/EmbedSearchWidgetContext';
import { embedSearchWidgetSearchJobsRoute } from '#/modules/embedSearchWidget/jobs/routing/embedSearchWidgetJobs.routes';
import { embedSearchWidgetRouteGroup } from '#/modules/embedSearchWidget/routing/embedSearchWidget.routes';
import { embedSearchWidgetSearchVolopsRoute } from '#/modules/embedSearchWidget/volops/routing/embedSearchWidgetVolops.routes';
import type { HorizonAppRouteGroupLoaderArgs } from '#/routing/classes/types/AppRouteGroupLoaderArgs';
import type { HorizonAppRouteComponentProps } from '#/routing/types/HorizonAppRouteComponentProps';

import { EmbedSearchWidgetLayout } from './EmbedSearchWidgetLayout';

export async function loader({
  request,
  url,
  context,
  params: { lang, embedSearchWidgetId },
}: HorizonAppRouteGroupLoaderArgs<typeof embedSearchWidgetRouteGroup>) {
  const embedSearchWidget = await embedSearchWidgetIsomorphicApiFetchWidget({
    request,
    context,
  })(embedSearchWidgetId);

  // We rely on the layout to redirect because if we add a new route to do so we'll
  // trigger 2 API requests to fetch the widget data instead of 1
  //
  // While this is not a huge problem, it does incur extra loading time for the user
  const isRootRedirectRoute =
    url.pathname === `/${lang}/embed-search-widget/${embedSearchWidgetId}`;

  if (isRootRedirectRoute) {
    switch (embedSearchWidget.listingType) {
      case 'VOLOP':
        return redirect(
          embedSearchWidgetSearchVolopsRoute.with({
            lang,
            embedSearchWidgetId,
          }),
        );
      case 'JOB':
        return redirect(
          embedSearchWidgetSearchJobsRoute.with({ lang, embedSearchWidgetId }),
        );
    }
  }

  return data(
    { embedSearchWidget },
    {
      headers: {
        'X-Widget-Origins': embedSearchWidget.origins.join(' '),
      },
    },
  );
}

export function headers({ parentHeaders, loaderHeaders }: HeadersArgs) {
  parentHeaders.set('X-Widget-Origins', loaderHeaders.get('X-Widget-Origins')!);
  return parentHeaders;
}

export default function EmbedSearchWidgetLayoutHorizonRoute({
  loaderData: { embedSearchWidget },
}: HorizonAppRouteComponentProps<typeof loader>) {
  return (
    <EmbedSearchWidgetContext value={embedSearchWidget}>
      <EmbedSearchWidgetLayout embedSearchWidget={embedSearchWidget} />
    </EmbedSearchWidgetContext>
  );
}
